import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Globe, Mail, Palette, UserCircle2, ArrowRight, ArrowLeft, Search, Check, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { brandStyle, BRAND } from "@/lib/brand";
import { VinconexLogo } from "@/components/VinconexMark";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STEPS = [
  { key: "website", title: "Your website", icon: Globe },
  { key: "leads", title: "Leads & tools", icon: Mail },
  { key: "brand", title: "Branding", icon: Palette },
  { key: "account", title: "Admin account", icon: UserCircle2 },
];

const INIT = {
  website_url: "", dealer_name: "", phone: "", address: "", city: "", state: "", zip: "", logo_url: "",
  crm_email: "", payment_calculator_url: "", accent_color: BRAND.blue,
  admin_name: "", admin_email: "", password: "", want_specialist_match: false,
};

const inputCls = "bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 placeholder:text-slate-600";

export default function Signup() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite") || "";
  const [invite, setInvite] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...INIT, invite_token: inviteToken });
  const [detecting, setDetecting] = useState(false);
  const [detected, setDetected] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/public/invites/${inviteToken}`)
      .then(({ data }) => {
        setInvite(data);
        setForm((f) => ({ ...f, admin_email: f.admin_email || data.email, dealer_name: f.dealer_name || data.dealer_name }));
        if (data.dealer_name) setDetected(true);
      })
      .catch(() => toast.error("That invite link was already used or has expired — you can still sign up below"));
  }, [inviteToken]);

  const detect = async () => {
    setDetecting(true);
    try {
      const { data } = await api.post("/public/detect-dealer", { website_url: form.website_url });
      setForm((f) => ({
        ...f, website_url: data.website_url, dealer_name: f.dealer_name || data.name,
        phone: f.phone || data.phone, address: f.address || data.address, logo_url: f.logo_url || data.logo_url,
      }));
      setDetected(true);
      toast.success(data.detected ? `Found ${data.name}` : "Website reachable — fill in the details below");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDetecting(false);
    }
  };

  const canNext = [
    () => form.website_url.trim() && form.dealer_name.trim(),
    () => true,
    () => /^#[0-9a-f]{6}$/i.test(form.accent_color),
    () => form.admin_name.trim() && form.admin_email.trim() && form.password.length >= 12,
  ][step]();

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/auth/signup", form);
      await refresh();
      toast.success("Welcome to Vinconex! Your dealership is set up — Vinconex will review and approve it shortly.");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const previewDealer = { name: form.dealer_name || "Your Dealership", logo_url: form.logo_url };

  return (
    <div className="min-h-screen bg-[#05070C] grid-noise text-white">
      <header className="h-16 border-b border-[#1C2233] flex items-center justify-between px-5 sm:px-8">
        <VinconexLogo variant="wordmark" className="h-6 w-auto" />
        <Link to="/login" data-testid="signup-login-link" className="text-sm text-slate-400 hover:text-white">Already on Vinconex? Sign in</Link>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-12 grid lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-4">
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Add your dealership</h1>
          {invite && (
            <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#0A8CF2]/40 bg-[#0A8CF2]/10 px-3 py-1.5 text-xs text-[#7FC3FF]" data-testid="signup-invite-badge">
              <Check className="h-3.5 w-3.5" /> Invited by Vinconex{invite.dealer_name ? ` · ${invite.dealer_name}` : ""}
            </div>
          )}
          <p className="text-slate-400 mt-3 text-sm leading-relaxed">Four quick steps. We pull your inventory automatically and you'll be printing QR stickers in minutes. Vinconex reviews every new dealership before its pages go live.</p>
          <ol className="mt-8 space-y-3">
            {STEPS.map((s, i) => (
              <li key={s.key} data-testid={`signup-step-${i + 1}`} className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${i === step ? "border-[#0A8CF2]/60 bg-[#0A8CF2]/10" : i < step ? "border-[#1C2233] bg-[#0B0F18] text-slate-300" : "border-[#1C2233] text-slate-500"}`}>
                <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${i < step ? "bg-emerald-500/20 text-emerald-400" : i === step ? "bg-[#0A8CF2] text-white" : "bg-slate-800 text-slate-400"}`}>
                  {i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <s.icon className="h-4 w-4" /> {s.title}
              </li>
            ))}
          </ol>
        </aside>

        <section className="lg:col-span-8">
          <div className="tracing-top rounded-2xl border border-[#1C2233] bg-[#0B0F18] p-7 sm:p-9 min-h-[420px]">
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }} data-testid={`signup-panel-${step + 1}`}>
                {step === 0 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="font-heading text-2xl font-extrabold">Where's your inventory?</h2>
                      <p className="text-sm text-slate-400 mt-1">Paste your dealership website. We'll detect your name, phone, address and logo, then sync vehicles from it.</p>
                    </div>
                    <div>
                      <Label className="text-slate-300 mb-1.5 block">Dealership website</Label>
                      <div className="flex gap-2">
                        <Input data-testid="signup-website-input" value={form.website_url} onChange={set("website_url")} placeholder="https://yourdealership.com" className={inputCls} />
                        <Button type="button" onClick={detect} disabled={detecting || !form.website_url.trim()} data-testid="signup-detect-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold shrink-0">
                          {detecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="mr-2 h-4 w-4" /> Detect</>}
                        </Button>
                      </div>
                    </div>
                    {(detected || form.dealer_name) && (
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                          <Label className="text-slate-300 mb-1.5 block">Dealership name</Label>
                          <Input data-testid="signup-name-input" value={form.dealer_name} onChange={set("dealer_name")} className={inputCls} />
                        </div>
                        <div>
                          <Label className="text-slate-300 mb-1.5 block">Phone</Label>
                          <Input data-testid="signup-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} />
                        </div>
                        <div>
                          <Label className="text-slate-300 mb-1.5 block">Street address</Label>
                          <Input data-testid="signup-address-input" value={form.address} onChange={set("address")} className={inputCls} />
                        </div>
                        <div className="sm:col-span-2 grid grid-cols-6 gap-3">
                          <div className="col-span-3">
                            <Label className="text-slate-300 mb-1.5 block">City</Label>
                            <Input data-testid="signup-city-input" value={form.city} onChange={set("city")} placeholder="Bartonsville" className={inputCls} />
                          </div>
                          <div className="col-span-1">
                            <Label className="text-slate-300 mb-1.5 block">State</Label>
                            <Input data-testid="signup-state-input" value={form.state} onChange={set("state")} placeholder="PA" maxLength={2} className={inputCls} />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-slate-300 mb-1.5 block">ZIP</Label>
                            <Input data-testid="signup-zip-input" value={form.zip} onChange={set("zip")} placeholder="18321" className={inputCls} />
                          </div>
                        </div>
                      </div>
                    )}
                    {!detected && !form.dealer_name && (
                      <button type="button" onClick={() => setDetected(true)} data-testid="signup-manual-btn" className="text-xs text-slate-500 hover:text-slate-300 underline">Enter details manually</button>
                    )}
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="font-heading text-2xl font-extrabold">Where should leads go?</h2>
                      <p className="text-sm text-slate-400 mt-1">Every shopper request is emailed to your CRM instantly as an ADF XML lead. You can change this later in Settings.</p>
                    </div>
                    <div>
                      <Label className="text-slate-300 mb-1.5 block">CRM lead inbox (ADF)</Label>
                      <Input data-testid="signup-crm-input" type="email" value={form.crm_email} onChange={set("crm_email")} placeholder="leads@yourdealership.com" className={inputCls} />
                    </div>
                    <div>
                      <Label className="text-slate-300 mb-1.5 block">Payment calculator URL (optional)</Label>
                      <Input data-testid="signup-calc-input" value={form.payment_calculator_url} onChange={set("payment_calculator_url")} placeholder="https://yourdealership.com/finance" className={inputCls} />
                      <p className="text-xs text-slate-500 mt-1.5">Leave blank to send shoppers to each vehicle's page on your site.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, want_specialist_match: !f.want_specialist_match }))}
                      data-testid="signup-addon-toggle"
                      aria-pressed={form.want_specialist_match}
                      className={`w-full text-left rounded-xl border p-4 transition-all ${form.want_specialist_match ? "border-[#0A8CF2] bg-[#0A8CF2]/10" : "border-[#1C2233] bg-[#0B0F18] hover:border-slate-600"}`}
                    >
                      <div className="flex items-start gap-3">
                        <span className={`mt-0.5 h-5 w-5 rounded-md border flex items-center justify-center shrink-0 ${form.want_specialist_match ? "bg-[#0A8CF2] border-[#0A8CF2]" : "border-slate-600"}`}>
                          {form.want_specialist_match && <Check className="h-3.5 w-3.5 text-white" />}
                        </span>
                        <div>
                          <div className="font-semibold flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-[#0A8CF2]" /> Add Specialist Match
                            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-tech">add-on</span>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            Shoppers take a 4-question style check and get paired with the specialist who fits how they buy; managers score follow-through from your CRM. Not part of the basic package — Vinconex activates it after review.
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="font-heading text-2xl font-extrabold">Make it yours</h2>
                      <p className="text-sm text-slate-400 mt-1">Your QR landing pages and stickers use your colors and logo — powered by Vinconex.</p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300 mb-1.5 block">Accent color</Label>
                        <div className="flex gap-2 items-center">
                          <input type="color" data-testid="signup-accent-input" value={form.accent_color} onChange={set("accent_color")} className="h-10 w-12 rounded border border-slate-800 bg-slate-950 cursor-pointer" />
                          <Input data-testid="signup-accent-hex-input" value={form.accent_color} onChange={set("accent_color")} className={`${inputCls} font-mono-tech uppercase`} />
                        </div>
                      </div>
                      <div>
                        <Label className="text-slate-300 mb-1.5 block">Logo URL (optional)</Label>
                        <Input data-testid="signup-logo-input" value={form.logo_url} onChange={set("logo_url")} placeholder="https://…/logo.png" className={inputCls} />
                      </div>
                    </div>
                    <div className="rounded-xl border border-[#2A2E3D] bg-[#0A0B0E] p-5" style={brandStyle(form.accent_color)} data-testid="signup-brand-preview">
                      <div className="flex items-center justify-between">
                        <BrandLogo dealer={previewDealer} />
                        <span className="text-xs text-slate-500 font-mono-tech">preview</span>
                      </div>
                      <div className="mt-4 flex gap-2">
                        <span className="px-4 py-2 rounded-md bg-brand text-white text-sm font-bold">Match me with a specialist</span>
                        <span className="px-4 py-2 rounded-md border border-brand/40 bg-brand/10 text-brand-soft text-sm">See new arrivals</span>
                      </div>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="font-heading text-2xl font-extrabold">Create your admin login</h2>
                      <p className="text-sm text-slate-400 mt-1">You'll be able to add managers from the dashboard. New dealerships are reviewed by Vinconex before public pages go live.</p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-2">
                        <Label className="text-slate-300 mb-1.5 block">Your name</Label>
                        <Input data-testid="signup-admin-name-input" value={form.admin_name} onChange={set("admin_name")} className={inputCls} />
                      </div>
                      <div>
                        <Label className="text-slate-300 mb-1.5 block">Work email</Label>
                        <Input data-testid="signup-admin-email-input" type="email" value={form.admin_email} onChange={set("admin_email")} className={inputCls} />
                      </div>
                      <div>
                        <Label className="text-slate-300 mb-1.5 block">Password (12+ characters — a short phrase works well)</Label>
                        <Input data-testid="signup-password-input" type="password" value={form.password} onChange={set("password")} className={inputCls} />
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center justify-between mt-10">
              {step > 0 ? (
                <Button variant="ghost" onClick={() => setStep((s) => s - 1)} data-testid="signup-back-btn" className="text-slate-400 hover:text-white"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
              ) : <span />}
              {step < STEPS.length - 1 ? (
                <Button onClick={() => setStep((s) => s + 1)} disabled={!canNext} data-testid="signup-next-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">Continue <ArrowRight className="ml-1 h-4 w-4" /></Button>
              ) : (
                <Button onClick={submit} disabled={!canNext || submitting} data-testid="signup-submit-btn" className="bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold h-11 px-6">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Launch my dealership"}
                </Button>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
